import * as React from 'react';
import { useDiagnosisPermission } from '../../../contexts/DiagnosisPermissionContext';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { Stack, Typography, Box, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import {
  DiagnosisCustomerCreateDTO,
  DiagnosisCustomerReadDTO,
  DiagnosisCustomerSitesCreateDTO,
  DiagnosisCustomerSitesReadDTO,
  DiagnosisSelfProductionPlantsReadDTO,
  DiagnosisSelfProductionPlantsCreateDTO,
} from '../../../generated/api-client';
import { useUpdateDiagnosisCustomer, useUpdateDiagnosisCustomerSites } from '../../../hooks';
import { customerFormSchema, type CustomerFormData } from '../../../schemas/customerFormSchema';
import { siteFormSchema, type SiteFormData } from '../../../schemas/siteFormSchema';
import { CustomerDetailMainTab } from '../../customer/CustomerDetailMainTab';
import { SiteDetailForm } from '../../site/SiteDetailForm';
import { DiagnosisStepHandle } from './Step.types';
import { apiClient } from '../../../services/apiClient';
import { CrudDataGridHandle } from '../../CrudDataGrid';
import {
  SelfProductionPlantsGrid,
  type SelfProductionPlantsGridProps,
} from '../../selfProduction/SelfProductionPlantsGrid';
import { ActionSpacer } from '../../common';
import { formatDateForServer } from '../../../utils/date.utils';

interface DiagnosisCustomerSiteStepProps {
  diagnosisCustomerId?: string;
  diagnosisSiteId?: string;
  diagnosisId?: string;
  customerData?: DiagnosisCustomerReadDTO | null;
  siteData?: DiagnosisCustomerSitesReadDTO | null;
}

const customerDefaultValues: CustomerFormData = {
  company_name: '',
  company_name_type: '',
  ateco_2025: '',
  address: '',
  phone: '',
  voluntary: null,
  type: '',
  multi_site: undefined,
  clustering: '',
  employee_count: null,
  revenue: null,
  balance_sheet_assets: null,
  size_category: '',
  consumption_category: '',
  average_annual_consumption: null,
  high_electricity_consumption: undefined,
  high_gas_consumption: undefined,
  certifications: '',
  vat_number: '',
  tax_code: '',
  certified_email: '',
  legal_representative: '',
  legal_representative_phone: '',
  legal_representative_email: '',
  contact_person: '',
  contact_person_phone: '',
  contact_person_email: '',
};

const siteDefaultValues: SiteFormData = {
  customer_id: undefined,
  address: '',
  iso_50001_certified_site: null,
  covered_area: null,
  telecom_network_presence: null,
  cogeneration_availability: null,
  site_framing: '',
  excluded_areas: '',
  norm_unicei_16247_1: true,
  norm_unicei_16247_2: false,
  norm_unicei_16247_3: false,
  norm_unicei_16247_4: false,
  notes: '',
  property: '',
  type: '',
  availability_until: null,
  construction_year: null,
  renovation_year: null,
  energy_performance_certificate: false,
  efficiency_class: '',
  building_envelope_walls_floors: '',
  building_envelope_insulation: '',
  building_envelope_transmittance: '',
  fixtures_glass: '',
  fixtures_frame: '',
  fixtures_transmittance: '',
  fixtures_shading: '',
  img: null,
  climatic_zone: undefined,
  start_summer: undefined,
  start_winter: undefined,
  has_podp: 0,
  has_podi: 0,
  has_prod: 0,
  has_pdr: 0,
};

const emptyToUndefined = (value: string | null | undefined) => {
  if (value === undefined || value === null) return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
};

const toNullableNumber = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const mapDiagnosisCustomerToForm = (
  entity?: DiagnosisCustomerReadDTO | null
): CustomerFormData => ({
  company_name: entity?.company_name ?? '',
  company_name_type: entity?.company_name_type ?? '',
  ateco_2025: entity?.ateco_2025 ?? '',
  address: entity?.address ?? '',
  phone: entity?.phone ?? '',
  voluntary: entity?.voluntary ?? null,
  type: entity?.type ?? '',
  multi_site: entity?.single_multi_site ?? null,
  clustering: entity?.clustering ?? '',
  employee_count: entity?.employee_count ?? null,
  revenue: entity?.revenue ?? null,
  balance_sheet_assets: entity?.balance_sheet_assets ?? null,
  size_category: entity?.size_category ?? '',
  consumption_category: entity?.consumption_category ?? '',
  average_annual_consumption: entity?.average_annual_consumption ?? null,
  high_electricity_consumption: entity?.high_electricity_consumption ?? undefined,
  high_gas_consumption: entity?.high_gas_consumption ?? undefined,
  certifications: entity?.certifications ?? '',
  vat_number: entity?.vat_number ?? '',
  tax_code: entity?.tax_code ?? '',
  certified_email: entity?.certified_email ?? '',
  legal_representative: entity?.legal_representative ?? '',
  legal_representative_phone: entity?.legal_representative_phone ?? '',
  legal_representative_email: entity?.legal_representative_email ?? '',
  contact_person: entity?.diagnosis_contact_person ?? '',
  contact_person_phone: entity?.contact_person_phone ?? '',
  contact_person_email: entity?.contact_person_email ?? '',
});

const mapFormToDiagnosisCustomerDTO = (form: CustomerFormData): DiagnosisCustomerCreateDTO =>
  new DiagnosisCustomerCreateDTO({
    company_name: form.company_name.trim(),
    company_name_type: emptyToUndefined(form.company_name_type),
    ateco_2025: emptyToUndefined(form.ateco_2025),
    address: emptyToUndefined(form.address),
    phone: emptyToUndefined(form.phone),
    voluntary: form.voluntary ?? (null as any),
    type: emptyToUndefined(form.type),
    single_multi_site:
      form.multi_site === null
        ? (null as unknown as boolean)
        : form.multi_site === undefined
          ? undefined
          : form.multi_site,
    clustering: emptyToUndefined(form.clustering),
    employee_count: toNullableNumber(form.employee_count),
    revenue: toNullableNumber(form.revenue),
    balance_sheet_assets: toNullableNumber(form.balance_sheet_assets),
    size_category: emptyToUndefined(form.size_category),
    consumption_category: emptyToUndefined(form.consumption_category),
    average_annual_consumption: toNullableNumber(form.average_annual_consumption),
    high_electricity_consumption:
      form.high_electricity_consumption === null
        ? (null as unknown as boolean)
        : form.high_electricity_consumption === undefined
          ? undefined
          : form.high_electricity_consumption,
    high_gas_consumption:
      form.high_gas_consumption === null
        ? (null as unknown as boolean)
        : form.high_gas_consumption === undefined
          ? undefined
          : form.high_gas_consumption,
    certifications: emptyToUndefined(form.certifications),
    vat_number: emptyToUndefined(form.vat_number),
    tax_code: emptyToUndefined(form.tax_code),
    certified_email: emptyToUndefined(form.certified_email),
    legal_representative: emptyToUndefined(form.legal_representative),
    legal_representative_phone: emptyToUndefined(form.legal_representative_phone),
    legal_representative_email: emptyToUndefined(form.legal_representative_email),
    diagnosis_contact_person: emptyToUndefined(form.contact_person),
    contact_person_phone: emptyToUndefined(form.contact_person_phone),
    contact_person_email: emptyToUndefined(form.contact_person_email),
  });

const mapDiagnosisSiteToForm = (entity?: DiagnosisCustomerSitesReadDTO | null): SiteFormData => ({
  customer_id: entity?.customer_id ?? undefined,
  address: entity?.address ?? '',
  iso_50001_certified_site: entity?.iso_50001_certified_site ?? null,
  covered_area: entity?.covered_area ?? null,
  telecom_network_presence: entity?.telecom_network_presence ?? null,
  cogeneration_availability: entity?.cogeneration_availability ?? null,
  site_framing: entity?.site_framing ?? '',
  excluded_areas: entity?.excluded_areas ?? '',
  norm_unicei_16247_1: entity?.norm_unicei_16247_1 ?? true,
  norm_unicei_16247_2: entity?.norm_unicei_16247_2 ?? false,
  norm_unicei_16247_3: entity?.norm_unicei_16247_3 ?? false,
  norm_unicei_16247_4: entity?.norm_unicei_16247_4 ?? false,
  notes: entity?.notes ?? '',
  property: entity?.property ?? '',
  type: entity?.type ?? '',
  availability_until: entity?.availability_until ? new Date(entity.availability_until) : null,
  construction_year: entity?.construction_year ? new Date(entity.construction_year) : null,
  renovation_year: entity?.renovation_year ? new Date(entity.renovation_year) : null,
  energy_performance_certificate: entity?.energy_performance_certificate ?? false,
  efficiency_class: entity?.efficiency_class ?? '',
  building_envelope_walls_floors: entity?.building_envelope_walls_floors ?? '',
  building_envelope_insulation: entity?.building_envelope_insulation ?? '',
  building_envelope_transmittance: entity?.building_envelope_transmittance ?? '',
  fixtures_glass: entity?.fixtures_glass ?? '',
  fixtures_frame: entity?.fixtures_frame ?? '',
  fixtures_transmittance: entity?.fixtures_transmittance ?? '',
  fixtures_shading: entity?.fixtures_shading ?? '',
  img: entity?.img ?? null,
  climatic_zone:
    entity?.climatic_zone && entity.climatic_zone.trim() !== ''
      ? entity.climatic_zone.trim()
      : undefined,
  start_summer:
    entity?.start_summer && entity.start_summer.trim() !== ''
      ? entity.start_summer.trim()
      : undefined,
  start_winter:
    entity?.start_winter && entity.start_winter.trim() !== ''
      ? entity.start_winter.trim()
      : undefined,
  has_podp: (entity as any)?.has_podp ?? 0,
  has_podi: (entity as any)?.has_podi ?? 0,
  has_prod: (entity as any)?.has_prod ?? 0,
  has_pdr: (entity as any)?.has_pdr ?? 0,
});

const mapFormToDiagnosisSiteDTO = (form: SiteFormData): DiagnosisCustomerSitesCreateDTO =>
  new DiagnosisCustomerSitesCreateDTO({
    customer_id: emptyToUndefined(form.customer_id),
    address: form.address,
    iso_50001_certified_site: form.iso_50001_certified_site as boolean,
    covered_area: toNullableNumber(form.covered_area),
    telecom_network_presence: form.telecom_network_presence as boolean,
    cogeneration_availability: form.cogeneration_availability as boolean,
    site_framing: emptyToUndefined(form.site_framing),
    excluded_areas: emptyToUndefined(form.excluded_areas),
    norm_unicei_16247_1: form.norm_unicei_16247_1 ?? true,
    norm_unicei_16247_2: form.norm_unicei_16247_2 ?? false,
    norm_unicei_16247_3: form.norm_unicei_16247_3 ?? false,
    norm_unicei_16247_4: form.norm_unicei_16247_4 ?? false,
    notes: emptyToUndefined(form.notes),
    property: emptyToUndefined(form.property),
    type: emptyToUndefined(form.type),
    availability_until: formatDateForServer(form.availability_until),
    construction_year: formatDateForServer(form.construction_year),
    renovation_year: formatDateForServer(form.renovation_year),
    energy_performance_certificate: form.energy_performance_certificate ?? false,
    efficiency_class: emptyToUndefined(form.efficiency_class),
    building_envelope_walls_floors: emptyToUndefined(form.building_envelope_walls_floors),
    building_envelope_insulation: emptyToUndefined(form.building_envelope_insulation),
    building_envelope_transmittance: emptyToUndefined(form.building_envelope_transmittance),
    fixtures_glass: emptyToUndefined(form.fixtures_glass),
    fixtures_frame: emptyToUndefined(form.fixtures_frame),
    fixtures_transmittance: emptyToUndefined(form.fixtures_transmittance),
    fixtures_shading: emptyToUndefined(form.fixtures_shading),
    climatic_zone: emptyToUndefined(form.climatic_zone),
    start_summer: emptyToUndefined(form.start_summer),
    start_winter: emptyToUndefined(form.start_winter),
    has_podp: form.has_podp ?? 0,
    has_podi: form.has_podi ?? 0,
    has_prod: form.has_prod ?? 0,
    has_pdr: form.has_pdr ?? 0,
  });

export const DiagnosisCustomerSiteStep = React.memo(
  forwardRef<DiagnosisStepHandle, DiagnosisCustomerSiteStepProps>(
    ({ diagnosisCustomerId, diagnosisSiteId, diagnosisId, customerData, siteData }, ref) => {
      const customerFormMethods = useForm<CustomerFormData>({
        defaultValues: customerDefaultValues,
        resolver: zodResolver(customerFormSchema),
        mode: 'onChange',
      });

      const siteFormMethods = useForm<SiteFormData>({
        defaultValues: siteDefaultValues,
        resolver: zodResolver(siteFormSchema),
        mode: 'onChange',
      });

      // Watch tutti i campi per forzare il re-render quando cambiano
      customerFormMethods.watch();
      siteFormMethods.watch();

      const queryClient = useQueryClient();
      const updateDiagnosisCustomer = useUpdateDiagnosisCustomer();
      const updateDiagnosisSite = useUpdateDiagnosisCustomerSites();

      const gridProps = React.useMemo<SelfProductionPlantsGridProps<
        DiagnosisSelfProductionPlantsReadDTO,
        DiagnosisSelfProductionPlantsCreateDTO
      > | null>(() => {
        if (!diagnosisId) {
          return null;
        }
        return {
          queryKey: `diagnosis-self-production-plants-${diagnosisId}`,
          listFn: () =>
            apiClient.search_by_diagnosis_id_diagnosis_self_production_plants_search_by_diagnosis_id__diagnosis_id__get(
              diagnosisId
            ),
          createFn: data =>
            apiClient.create_item_diagnosis_self_production_plants__post(
              new DiagnosisSelfProductionPlantsCreateDTO({
                ...data,
                diagnosis_id: diagnosisId,
              })
            ),
          updateFn: (id, data) =>
            apiClient.update_item_diagnosis_self_production_plants__item_id__put(
              id,
              new DiagnosisSelfProductionPlantsCreateDTO({
                ...data,
                diagnosis_id: diagnosisId,
              })
            ),
          deleteFn: id =>
            apiClient.delete_item_diagnosis_self_production_plants__item_id__delete(id),
          getId: row => row.id ?? '',
          getCreateDto: row =>
            new DiagnosisSelfProductionPlantsCreateDTO({
              diagnosis_id: diagnosisId,
              type: row.type ?? undefined,
              power_source: row.power_source ?? undefined,
              in_service_year: formatDateForServer(row.in_service_year),
              electrical_power_kw: row.electrical_power_kw
                ? Number(row.electrical_power_kw)
                : undefined,
              thermal_power_kw: row.thermal_power_kw ? Number(row.thermal_power_kw) : undefined,
              cooling_power_kw: row.cooling_power_kw ? Number(row.cooling_power_kw) : undefined,
            }),
        };
      }, [diagnosisId]);

      const gridHandleRef = useRef<CrudDataGridHandle>(null);
      const mapContainerRef = useRef<HTMLDivElement>(null);
      const [siteImage, setSiteImage] = React.useState<string | null>(null);

      useEffect(() => {
        if (customerData) {
          customerFormMethods.reset(mapDiagnosisCustomerToForm(customerData));
        } else {
          customerFormMethods.reset(customerDefaultValues);
        }
      }, [customerData, customerFormMethods]);

      useEffect(() => {
        if (siteData) {
          siteFormMethods.reset(mapDiagnosisSiteToForm(siteData));
          setSiteImage(siteData.img ?? null);
        } else {
          siteFormMethods.reset(siteDefaultValues);
          setSiteImage(null);
        }
      }, [siteData, siteFormMethods]);

      const performSave = async () => {
        if (!diagnosisCustomerId && !diagnosisSiteId) {
          return { success: true };
        }

        try {
          // Salva cliente se presente
          if (diagnosisCustomerId) {
            const customerPayload = mapFormToDiagnosisCustomerDTO(customerFormMethods.getValues());
            await updateDiagnosisCustomer.mutateAsync({
              id: diagnosisCustomerId,
              data: customerPayload,
            });
          }

          // Salva sito se presente
          if (diagnosisSiteId) {
            const sitePayload = mapFormToDiagnosisSiteDTO(siteFormMethods.getValues());
            await updateDiagnosisSite.mutateAsync({
              id: diagnosisSiteId,
              data: sitePayload,
            });
            // Aggiornare Grafici POD: creando POD da Sito (has_podp/has_podi) il backend crea le colonne
            if (diagnosisId) {
              queryClient.invalidateQueries(`pod-timetables-${diagnosisId}`);
            }
          }

          toast.success('Dati cliente e sito salvati');
          return { success: true };
        } catch (error: any) {
          const message = error?.message || 'Errore nel salvataggio';
          toast.error(message);
          return { success: false };
        }
      };

      React.useImperativeHandle(ref, () => ({
        save: async (force = false) => {
          const customerValid =
            force || (await customerFormMethods.trigger(undefined, { shouldFocus: true }));
          const siteValid =
            force || (await siteFormMethods.trigger(undefined, { shouldFocus: true }));

          if (!customerValid || !siteValid) {
            if (!force) {
              return { success: false, requiresConfirmation: true };
            }
          }
          return performSave();
        },
      }));

      return (
        <Stack spacing={3} sx={{ p: 2 }}>
          <FormProvider {...customerFormMethods}>
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Cliente
              </Typography>
              <CustomerDetailMainTab />
            </Box>
          </FormProvider>

          <FormProvider {...siteFormMethods}>
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Sito
              </Typography>
              <SiteDetailForm
                showMap={true}
                mapContainerRef={mapContainerRef}
                siteId={diagnosisSiteId}
                diagnosisId={diagnosisId}
                img={siteImage}
                onChangeImg={setSiteImage}
                isDiagnosisSite={true}
              />
            </Box>
          </FormProvider>

          {diagnosisId && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Impianti di autoproduzione
              </Typography>
              {gridProps && (
                <SelfProductionPlantsGrid ref={gridHandleRef} {...gridProps} height="400px" />
              )}
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  sx={{ borderRadius: '4px' }}
                  startIcon={<AddIcon />}
                  onClick={() => {
                    if (!diagnosisId) {
                      toast.error('Salva prima i dati principali della diagnosi.');
                      return;
                    }
                    gridHandleRef.current?.addRow();
                  }}
                >
                  Aggiungi impianto
                </Button>
              </Box>
            </Box>
          )}
          <ActionSpacer></ActionSpacer>
        </Stack>
      );
    }
  )
);

DiagnosisCustomerSiteStep.displayName = 'DiagnosisCustomerSiteStep';
